import React, { useCallback, useEffect, useState } from 'react'
import { TabletSmartphone, Timer, Plus, QrCode, RefreshCw } from 'lucide-react'
import { Trail } from '../navigation/Trail.js'
import { SmartBack } from '../navigation/SmartBack.js'
import { Modal, apiGet, adminMutate, useToast } from '../admin/primitives.js'
import { Field, TextInput } from '../admin/form.js'
import { humanName } from './format.js'
import { useMenuPersonne } from './menu-personne.js'
import './rh.css'

/**
 * SIRH LOTS 4-5 — pages de GESTION du module Temps : Pointeuse (administration),
 * Congés & absences, Disponibilités. (Lot 4.6 : Validation → ValidationWorkstation.tsx,
 * Compteurs → CountersPages.tsx.)
 * Doctrine product-reset : pages de gestion pleine page (mg-*), zéro
 * mini-table dans une tuile ; doctrine Chrome UX QA appliquée à la recette.
 */


/* Adaptateurs locaux : toast (kind,texte), adminMutate ({status,data}) et
   Modal (ariaLabel) du design system, avec l'ergonomie compacte des pages Temps. */
function useNotify(): (msg: string, kind?: 'success' | 'error' | 'info') => void {
  const raw = useToast()
  // STABLE entre les renders : ce hook entre dans les deps des useCallback de
  // chargement — une identité neuve à chaque render créerait une boucle infinie
  // de requêtes (vu en recette Chrome, corrigé ici).
  return useCallback((msg: string, kind: 'success' | 'error' | 'info' = 'success') => raw(kind === 'error' ? 'error' : 'ok', msg), [raw])
}
async function mutate<T extends { error?: string; message?: string }>(method: string, path: string, body?: unknown): Promise<T> {
  const r = await adminMutate<T>(method, path, body)
  if (r.status >= 400 && !(r.data as { error?: string }).error) {
    return { ...(r.data as T), error: 'ERROR' }
  }
  return r.data
}
function MModal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <Modal open onClose={onClose} ariaLabel={title} width={560}>
      <div className="org-editor">
        <h3>{title}</h3>
        {children}
      </div>
    </Modal>
  )
}

/* ─── helpers ─── */
const frShort = (ymd: string): string =>
  new Date(`${ymd}T12:00:00Z`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', timeZone: 'Europe/Paris' })

/* ═══════════════════════════ POINTEUSE (administration) ═══════════════════════════ */

interface StationRow {
  id: string; name: string; version: string | null; location_name: string | null
  allowed_methods: string[]; last_seen_at: string | null; pending_events: number
  online: boolean; revoked_at: string | null
  /** Niveau de sécurité — une borne héritée n'est PAS une borne native dégradée. */
  security: 'NATIVE_SECURISEE' | 'LEGACY_A_REAPPAIRER' | 'REVOQUEE'
  device_bound: boolean; token_expired: boolean
  device_key_fingerprint: string | null; device_id: string | null
  token_expires_at: string | null; purge_requested_at: string | null
}

/**
 * L'état de sécurité, dit en toutes lettres.
 *
 * Une borne sans clé d'appareil ne peut signer aucune requête : elle ne peut
 * donc plus rien faire de sensible. La présenter comme « un peu ancienne »
 * laisserait croire qu'elle fonctionne encore.
 */
interface CredentialStatus {
  person_id: string; display_name: string; kind: string
  created_at: string; pin_length: number | null; offline_ready: boolean
}

const SECURITE: Record<StationRow['security'], { texte: string; ton: string }> = {
  NATIVE_SECURISEE: { texte: 'Native sécurisée', ton: 'ok' },
  LEGACY_A_REAPPAIRER: { texte: 'Héritée — à réappairer', ton: 'warn' },
  REVOQUEE: { texte: 'Révoquée', ton: 'off' },
}

export function HrPointeuseAdminPage() {
  const toast = useNotify()
  const menu = useMenuPersonne()
  const [stations, setStations] = useState<StationRow[]>([])
  const [creds, setCreds] = useState<CredentialStatus[]>([])
  // Un code au format hérité : longueur inconnue, donc hors ligne impossible.
  const credsLegacy = creds.filter((c) => c.kind === 'PIN' && c.pin_length === null)
  const [policy, setPolicy] = useState<{ geo_mode: string; default_break_paid?: boolean } | null>(null)
  const [pairing, setPairing] = useState<{ code: string; expires_at: string } | null>(null)
  const [people, setPeople] = useState<Array<{ id: string; display_name: string }>>([])
  const [credPerson, setCredPerson] = useState('')
  const [credPin, setCredPin] = useState('')
  const [qr, setQr] = useState<string | null>(null)

  const load = useCallback(() => {
    apiGet<{ stations: StationRow[] }>('/api/v1/hr/time/stations').then((r) => setStations(r.stations)).catch(() => {})
    apiGet<{ credentials: CredentialStatus[] }>('/api/v1/hr/time/credentials').then((r) => setCreds(r.credentials)).catch(() => {})
    apiGet<{ policy: { geo_mode: string; default_break_paid?: boolean } }>('/api/v1/hr/time/policy').then((r) => setPolicy(r.policy)).catch(() => {})
    apiGet<{ people: Array<{ id: string; display_name: string }> }>('/api/v1/rh/people').then((r) => setPeople(r.people)).catch(() => {})
  }, [])
  useEffect(() => { load() }, [load])

  async function newPairing() {
    const r = await mutate<{ error?: string; message?: string; code?: string; expires_at?: string }>('POST', '/api/v1/hr/time/stations/pairing-code', {})
    if (r.error || !r.code) { toast(r.message ?? 'Échec.', 'error'); return }
    setPairing({ code: r.code, expires_at: r.expires_at! })
  }
  const [revoking, setRevoking] = useState<{ id: string; name: string } | null>(null)
  async function revoke() {
    if (!revoking) return
    const r = await mutate<{ error?: string; message?: string }>('DELETE', `/api/v1/hr/time/stations/${revoking.id}`, {})
    if (r.error) { toast(r.message ?? 'Échec.', 'error'); return }
    toast('Station révoquée.', 'success'); setRevoking(null); load()
  }
  async function setPin() {
    // MÊME règle que le serveur, au caractère près : proposer 4 à 6 alors que
    // le serveur en exige exactement 4 ferait échouer une saisie que l'écran
    // venait d'inviter.
    if (!credPerson || !/^\d{4}$/.test(credPin)) { toast('Choisissez un salarié et un code de exactement 4 chiffres.', 'error'); return }
    const r = await mutate<{ error?: string; message?: string }>('POST', `/api/v1/hr/time/credentials/${credPerson}/pin`, { pin: credPin })
    if (r.error) { toast(r.message ?? 'Échec.', 'error'); return }
    toast('Code personnel défini.', 'success'); setCredPin('')
  }
  async function makeQr() {
    if (!credPerson) { toast('Choisissez un salarié.', 'error'); return }
    const r = await mutate<{ error?: string; message?: string; data_url?: string }>('POST', `/api/v1/hr/time/credentials/${credPerson}/qr`, {})
    if (r.error || !r.data_url) { toast(r.message ?? 'Échec.', 'error'); return }
    setQr(r.data_url)
  }

  const ago = (iso: string | null) => {
    if (!iso) return 'jamais'
    const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000)
    return s < 60 ? `il y a ${s} s` : s < 3600 ? `il y a ${Math.round(s / 60)} min` : `il y a ${Math.round(s / 3600)} h`
  }

  return (
    <main className="lc-page lc-page--management lc-stagger">
      <Trail />
      <SmartBack fallback="/equipe/temps" />
      <header className="mg-head tv-head">
        <div>
          <h1 className="lc-title">Pointeuse</h1>
          <p className="lc-subtitle">Stations kiosk, codes personnels et politique de pointage. UniFi Access n’est jamais une pointeuse.</p>
        </div>
        <button className="lc-btn lc-btn--primary" onClick={() => void newPairing()}><Plus size={15} aria-hidden="true" /> Ajouter une station</button>
      </header>

      <section className="lc-card tv-block">
        <h2 className="tv-block__title"><TabletSmartphone size={16} aria-hidden="true" /> Stations</h2>
        {stations.length === 0 ? <p className="lc-hint">Aucune station. « Ajouter une station » génère un code d’appairage à saisir sur l’iPad (adresse : gestion.liveclubrennes.com/pointeuse).</p> : (
          <div className="mg-list">
            {stations.map((s) => (
              <div key={s.id} className="mg-row mg-row--static">
                <div className="mg-row__main">
                  <strong>{s.revoked_at ? <s>{s.name}</s> : s.name}</strong>
                  <span className="mg-row__desc">
                    <span className={`lc-dot ${s.online ? 'lc-dot--ok' : 'lc-dot--idle'}`} /> {s.revoked_at ? 'Révoquée' : s.online ? 'En ligne' : 'Hors ligne'}
                    {s.location_name ? ` · ${s.location_name}` : ''} · v{s.version ?? '?'} · dernière synchro {ago(s.last_seen_at)}
                    {s.pending_events > 0 ? ` · ${s.pending_events} pointage(s) en attente` : ''}
                    {` · ${s.allowed_methods.join(' + ')}`}
                  </span>
                  <span className="mg-row__desc tv-station__secu">
                    <span className={`tv-secu tv-secu--${SECURITE[s.security].ton}`}>{SECURITE[s.security].texte}</span>
                    {s.device_bound
                      ? <> · clé <code>{s.device_key_fingerprint}</code></>
                      : <> · aucune clé d’appareil : cette borne ne peut signer aucune requête</>}
                    {s.device_id ? <> · appareil <code>{s.device_id}</code></> : null}
                    {s.token_expires_at
                      ? <> · jeton {s.token_expired ? 'EXPIRÉ' : `valable jusqu’au ${new Date(s.token_expires_at).toLocaleDateString('fr-FR')}`}</>
                      : null}
                    {s.purge_requested_at ? <> · purge locale demandée</> : null}
                  </span>
                </div>
                {!s.revoked_at ? (
                  <div className="mg-row__facts">
                    <button className="lc-btn lc-btn--danger" onClick={() => setRevoking({ id: s.id, name: s.name })}>Révoquer</button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="lc-card tv-block">
        <h2 className="tv-block__title"><QrCode size={16} aria-hidden="true" /> Codes de pointage des salariés</h2>
        <div className="tv-methods">
          <span className="tv-method tv-method--ok">PIN <em>Disponible</em></span>
          <span className="tv-method tv-method--soon">QR <em>Scanner caméra à venir</em></span>
        </div>
        <p className="lc-hint">Le PIN est la méthode opérationnelle (jamais stocké en clair). Le QR est prêt côté serveur (identifiant opaque révocable, aucune donnée personnelle) mais le <strong>scanner caméra du kiosk n’est pas encore branché</strong> : le QR généré n’est pas exploitable en production tant que ce lecteur n’existe pas.</p>
        <div className="tv-credrow">
          <select className="lc-input" value={credPerson} onChange={(e) => setCredPerson(e.target.value)} aria-label="Salarié">
            <option value="">Choisir un salarié…</option>
            {people.map((p) => <option key={p.id} value={p.id}>{humanName(p.display_name)}</option>)}
          </select>
          {credsLegacy.length > 0 ? (
            <div className="tv-legacy">
              <strong>Code de pointage à redéfinir</strong>
              <p className="lc-hint">
                {credsLegacy.length === 1 ? 'Ce code a été défini' : 'Ces codes ont été définis'} avant le format
                actuel de quatre chiffres. {credsLegacy.length === 1 ? 'Il continue' : 'Ils continuent'} de
                fonctionner en ligne, mais ne {credsLegacy.length === 1 ? 'permet' : 'permettent'} pas
                l’identification hors connexion. Gestion ne peut pas en connaître la longueur — une empreinte
                ne la révèle pas — et ne le remplacera jamais tout seul : choisissez un nouveau code
                <em> avec la personne concernée</em>.
              </p>
              <ul className="tv-legacy__liste">
                {credsLegacy.map((c) => (
                  <li key={c.person_id} {...menu.personne({ id: c.person_id, nom: c.display_name })}>
                    <span>{c.display_name}</span>
                    <button type="button" className="lc-btn lc-btn--ghost"
                      onClick={() => { setCredPerson(c.person_id); setCredPin('') }}>
                      Définir un nouveau code à 4 chiffres
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <input className="lc-input tv-pininput" value={credPin} inputMode="numeric" maxLength={4}
            onChange={(e) => setCredPin(e.target.value.replace(/\D/g, ''))} placeholder="PIN (4 chiffres)" aria-label="PIN" />
          <button className="lc-btn lc-btn--primary" onClick={() => void setPin()}>Définir le PIN</button>
          <button className="lc-btn" onClick={() => void makeQr()} title="Le scanner caméra n’est pas encore branché : QR non exploité en production.">Générer un QR (aperçu)</button>
        </div>
      </section>

      <section className="lc-card tv-block">
        <h2 className="tv-block__title"><Timer size={16} aria-hidden="true" /> Politique de pointage</h2>
        <label className="tv-fieldlabel">Géolocalisation</label>
        <p className="lc-hint">Uniquement AU MOMENT du pointage distant, jamais de suivi continu. OFF tant que la Direction ne l’active pas explicitement.</p>
        <select className="lc-input" value={policy?.geo_mode ?? 'OFF'} aria-label="Géolocalisation"
          onChange={(e) => {
            void mutate('PUT', '/api/v1/hr/time/policy', { geo_mode: e.target.value }).then(() => { toast('Politique enregistrée.', 'success'); load() })
          }}>
          <option value="OFF">Désactivée (défaut)</option>
          <option value="POINT_IN_TIME_OPTIONAL">Ponctuelle, facultative</option>
          <option value="POINT_IN_TIME_REQUIRED">Ponctuelle, requise pour le pointage distant</option>
        </select>
        <label className="org-leadcheck" style={{ marginTop: 'var(--sp-3)' }}>
          <input type="checkbox" checked={policy?.default_break_paid ?? true}
            onChange={(e) => { void mutate('PUT', '/api/v1/hr/time/policy', { default_break_paid: e.target.checked }).then(() => { toast('Politique enregistrée.', 'success'); load() }) }} />
          Pauses rémunérées par défaut (nouveaux shifts)
        </label>
        <p className="lc-hint">Un shift de 7 h avec 20 min de pause rémunérée compte 7 h payées ; non rémunérée, 6 h 40. Réglable shift par shift.</p>
      </section>

      {revoking ? (
        <MModal title={`Révoquer « ${revoking.name} » ?`} onClose={() => setRevoking(null)}>
          <p className="lc-hint">La station ne pourra plus pointer dès son prochain contact. Les événements déjà synchronisés restent.</p>
          <div className="lc-modal__actions">
            <button className="lc-btn" onClick={() => setRevoking(null)}>Annuler</button>
            <button className="lc-btn lc-btn--danger" onClick={() => void revoke()}>Révoquer</button>
          </div>
        </MModal>
      ) : null}

      {pairing ? (
        <MModal title="Associer une station" onClose={() => { setPairing(null); load() }}>
          <p>Sur l’iPad, ouvrez <strong>gestion.liveclubrennes.com/pointeuse</strong> puis saisissez ce code :</p>
          <p className="tv-paircode">{pairing.code}</p>
          <p className="lc-hint">Valable 10 minutes, usage unique. Le jeton de station généré est dédié et révocable — jamais un compte utilisateur.</p>
          <div className="lc-modal__actions"><button className="lc-btn lc-btn--primary" onClick={() => { setPairing(null); load() }}>Fermer</button></div>
        </MModal>
      ) : null}

      {qr ? (
        <MModal title="QR de pointage" onClose={() => setQr(null)}>
          <img src={qr} alt="QR de pointage" className="tv-qr" />
          <p className="lc-hint">À imprimer ou enregistrer sur le téléphone du salarié. Regénérer un QR révoque le précédent.</p>
          <div className="lc-modal__actions"><button className="lc-btn lc-btn--primary" onClick={() => setQr(null)}>Fermer</button></div>
        </MModal>
      ) : null}
    </main>
  )
}

/* ═══════════════════════════ CONGÉS & ABSENCES ═══════════════════════════ */

interface LeaveRow {
  id: string; person_id: string; display_name: string; type_label: string
  starts_on: string; ends_on: string; start_half: boolean; end_half: boolean
  /** Une note existe — son contenu ne sort JAMAIS dans la liste (détail sous le droit « Voir les justificatifs d'absence »). Absent = inconnu. */
  comment_visible?: boolean | undefined
  status: string; requested_at: string; decided_at: string | null; decision_note: string | null
}

export function HrCongesPage() {
  const toast = useNotify()
  const menu = useMenuPersonne()
  const [tab, setTab] = useState<'pending' | 'history'>('pending')
  const [rows, setRows] = useState<LeaveRow[]>([])
  const load = useCallback(() => {
    apiGet<{ requests: LeaveRow[] }>('/api/v1/hr/leave/requests').then((r) => setRows(r.requests)).catch(() => {})
  }, [])
  useEffect(() => { load() }, [load])

  const [rejecting, setRejecting] = useState<string | null>(null)
  const [rejectNote, setRejectNote] = useState('')
  async function decide(id: string, decision: 'APPROVED' | 'REJECTED', note?: string) {
    const r = await mutate<{ error?: string; message?: string; planning_conflicts?: number }>(
      'POST', `/api/v1/hr/leave/requests/${id}/decide`, { decision, ...(note ? { note } : {}) })
    if (r.error) { toast(r.message ?? 'Échec.', 'error'); return }
    if (decision === 'APPROVED' && (r.planning_conflicts ?? 0) > 0) {
      toast(`Absence approuvée — ${r.planning_conflicts} shift(s) planifié(s) sur la période : à trancher dans le Planning.`, 'info')
    } else toast(decision === 'APPROVED' ? 'Absence approuvée.' : 'Demande refusée.', 'success')
    setRejecting(null); setRejectNote(''); load()
  }

  const visible = rows.filter((r) => tab === 'pending' ? r.status === 'REQUESTED' : r.status !== 'REQUESTED')
  return (
    <main className="lc-page lc-page--management lc-stagger">
      <Trail />
      <SmartBack fallback="/equipe/temps" />
      <header className="mg-head tv-head">
        <div>
          <h1 className="lc-title">Congés &amp; absences</h1>
          <p className="lc-subtitle">Les demandes des salariés, à approuver ou refuser. Une absence approuvée est visible au Planning.</p>
        </div>
      </header>
      <nav className="lc-tabs rh-tabs" aria-label="Filtre">
        <button className={`lc-tabs__tab${tab === 'pending' ? ' lc-tabs__tab--active' : ''}`} onClick={() => setTab('pending')}>À traiter ({rows.filter((r) => r.status === 'REQUESTED').length})</button>
        <button className={`lc-tabs__tab${tab === 'history' ? ' lc-tabs__tab--active' : ''}`} onClick={() => setTab('history')}>Historique</button>
      </nav>
      {visible.length === 0 ? <p className="lc-hint">{tab === 'pending' ? 'Aucune demande en attente.' : 'Aucune demande passée.'}</p> : (
        <div className="mg-list lc-card">
          {visible.map((r) => (
            <div key={r.id} className="mg-row mg-row--static" {...menu.personne({ id: r.person_id, nom: r.display_name })}>
              <div className="mg-row__main">
                <strong>{humanName(r.display_name)} — {r.type_label}</strong>
                <span className="mg-row__desc">
                  Du {frShort(r.starts_on)}{r.start_half ? ' (après-midi)' : ''} au {frShort(r.ends_on)}{r.end_half ? ' (matin)' : ''}
                  {r.comment_visible === true ? ' · Une note existe — voir le détail de l’absence' : ''}
                  {r.status !== 'REQUESTED' ? ` · ${r.status === 'APPROVED' ? 'Approuvée' : r.status === 'REJECTED' ? 'Refusée' : 'Annulée'}${r.decision_note ? ` — ${r.decision_note}` : ''}` : ''}
                </span>
              </div>
              {r.status === 'REQUESTED' ? (
                <div className="mg-row__facts">
                  <button className="lc-btn lc-btn--primary" onClick={() => void decide(r.id, 'APPROVED')}>Approuver</button>
                  <button className="lc-btn" onClick={() => { setRejecting(r.id); setRejectNote('') }}>Refuser</button>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
      {rejecting ? (
        <MModal title="Refuser la demande" onClose={() => setRejecting(null)}>
          <Field label="Motif du refus (visible par le salarié)"><TextInput value={rejectNote} onChange={setRejectNote} autoFocus /></Field>
          <div className="lc-modal__actions">
            <button className="lc-btn" onClick={() => setRejecting(null)}>Annuler</button>
            <button className="lc-btn lc-btn--primary" disabled={!rejectNote.trim()} onClick={() => void decide(rejecting, 'REJECTED', rejectNote.trim())}>Refuser la demande</button>
          </div>
        </MModal>
      ) : null}
    </main>
  )
}

/* ═══════════════════════════ DISPONIBILITÉS ═══════════════════════════ */

interface CampaignRow {
  id: string; title: string; target_date: string; message: string | null
  respond_until: string; status: string
  targets: number; responded: number; available: number; partial: number; unavailable: number
}

export function HrDisposPage() {
  const toast = useNotify()
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([])
  const [people, setPeople] = useState<Array<{ id: string; display_name: string }>>([])
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState({ title: '', target_date: '', respond_until: '', message: '', person_ids: [] as string[] })
  const [detail, setDetail] = useState<{ campaign: CampaignRow; rows: Array<{ person_id: string; display_name: string; status: string | null; from_minutes: number | null; to_minutes: number | null; comment: string | null }> } | null>(null)

  const load = useCallback(() => {
    apiGet<{ campaigns: CampaignRow[] }>('/api/v1/hr/availability/campaigns').then((r) => setCampaigns(r.campaigns)).catch(() => {})
    apiGet<{ people: Array<{ id: string; display_name: string }> }>('/api/v1/rh/people').then((r) => setPeople(r.people)).catch(() => {})
  }, [])
  useEffect(() => { load() }, [load])

  async function create() {
    const ids = form.person_ids.length ? form.person_ids : people.map((p) => p.id)
    const r = await mutate<{ error?: string; message?: string; notified?: number }>('POST', '/api/v1/hr/availability/campaigns', {
      title: form.title, target_date: form.target_date,
      respond_until: `${form.respond_until}T23:59:00.000Z`,
      ...(form.message.trim() ? { message: form.message.trim() } : {}),
      person_ids: ids,
    })
    if (r.error) { toast(r.message ?? 'Échec.', 'error'); return }
    toast(`Campagne créée — ${r.notified ?? 0} salarié(s) notifié(s) (ciblés uniquement).`, 'success')
    setCreating(false); setForm({ title: '', target_date: '', respond_until: '', message: '', person_ids: [] }); load()
  }
  async function remind(id: string) {
    const r = await mutate<{ error?: string; message?: string; reminded?: number }>('POST', `/api/v1/hr/availability/campaigns/${id}/remind`, {})
    if (r.error) { toast(r.message ?? 'Échec.', 'error'); return }
    toast(`Rappel envoyé à ${r.reminded ?? 0} non-répondant(s) — personne d'autre.`, 'success')
  }
  const fmtPartial = (from: number | null, to: number | null) => {
    const f = (m: number) => `${String(Math.floor((m % 1440) / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
    return from != null && to != null ? `${f(from)} → ${f(to)}` : ''
  }

  return (
    <main className="lc-page lc-page--management lc-stagger">
      <Trail />
      <SmartBack fallback="/equipe/temps" />
      <header className="mg-head tv-head">
        <div>
          <h1 className="lc-title">Disponibilités</h1>
          <p className="lc-subtitle">Ouvertures exceptionnelles : demandez, les salariés répondent depuis leur téléphone. Fini WhatsApp.</p>
        </div>
        <button className="lc-btn lc-btn--primary" onClick={() => setCreating(true)}><Plus size={15} aria-hidden="true" /> Nouvelle demande</button>
      </header>

      {campaigns.length === 0 ? <p className="lc-hint">Aucune campagne. Créez une demande de disponibilités pour une date d’ouverture exceptionnelle.</p> : (
        <div className="mg-list lc-card">
          {campaigns.map((c) => (
            <button key={c.id} className="mg-row" onClick={() => {
              void apiGet<NonNullable<typeof detail>>(`/api/v1/hr/availability/campaigns/${c.id}`).then(setDetail)
            }}>
              <div className="mg-row__main">
                <strong>{c.title} — {frShort(c.target_date)}</strong>
                <span className="mg-row__desc">
                  {c.responded}/{c.targets} réponses · ✓ {c.available} disponibles · ⚠ {c.partial} partiels · ✕ {c.unavailable} indisponibles · {c.targets - c.responded} sans réponse
                  {c.status !== 'OPEN' ? ' · close' : ''}
                </span>
              </div>
              <div className="mg-row__facts"><span className="mg-row__count">Détail →</span></div>
            </button>
          ))}
        </div>
      )}

      {creating ? (
        <MModal title="Demander des disponibilités" onClose={() => setCreating(false)}>
          <Field label="Titre"><TextInput value={form.title} onChange={(v) => setForm({ ...form, title: v })} placeholder="Ouverture exceptionnelle — mercredi 16 septembre" /></Field>
          <Field label="Date concernée"><input type="date" className="lc-input" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} /></Field>
          <Field label="Répondre avant le"><input type="date" className="lc-input" value={form.respond_until} onChange={(e) => setForm({ ...form, respond_until: e.target.value })} /></Field>
          <Field label="Message (facultatif)"><TextInput value={form.message} onChange={(v) => setForm({ ...form, message: v })} placeholder="Merci d’indiquer vos disponibilités." /></Field>
          <Field label={`Population ciblée (${form.person_ids.length || 'tous les salariés'})`}>
            <select multiple className="lc-input tv-multiselect" value={form.person_ids}
              onChange={(e) => setForm({ ...form, person_ids: [...e.target.selectedOptions].map((o) => o.value) })}>
              {people.map((p) => <option key={p.id} value={p.id}>{humanName(p.display_name)}</option>)}
            </select>
          </Field>
          <div className="lc-modal__actions">
            <button className="lc-btn" onClick={() => setCreating(false)}>Annuler</button>
            <button className="lc-btn lc-btn--primary" disabled={!form.title.trim() || !form.target_date || !form.respond_until} onClick={() => void create()}>Envoyer la demande</button>
          </div>
        </MModal>
      ) : null}

      {detail ? (
        <MModal title={`${detail.campaign.title} — ${frShort(detail.campaign.target_date)}`} onClose={() => setDetail(null)}>
          <div className="tv-movs">
            {detail.rows.map((r) => (
              <div key={r.person_id} className="tv-mov">
                <span className={`tv-availchip tv-availchip--${r.status ?? 'none'}`}>
                  {r.status === 'AVAILABLE' ? '✓' : r.status === 'PARTIAL' ? '⚠' : r.status === 'UNAVAILABLE' ? '✕' : '…'}
                </span>
                <span className="tv-mov__body">
                  {humanName(r.display_name)}
                  <em>{r.status === 'PARTIAL' ? `Disponible ${fmtPartial(r.from_minutes, r.to_minutes)}` : r.status === 'AVAILABLE' ? 'Disponible' : r.status === 'UNAVAILABLE' ? 'Indisponible' : 'Sans réponse'}{r.comment ? ` · « ${r.comment} »` : ''}</em>
                </span>
              </div>
            ))}
          </div>
          {detail.campaign.status === 'OPEN' ? (
            <div className="lc-modal__actions">
              <button className="lc-btn" onClick={() => void remind(detail.campaign.id)}><RefreshCw size={14} aria-hidden="true" /> Relancer les non-répondants</button>
              <button className="lc-btn" onClick={() => {
                void mutate('POST', `/api/v1/hr/availability/campaigns/${detail.campaign.id}/close`, {}).then(() => { setDetail(null); load() })
              }}>Clore</button>
            </div>
          ) : null}
        </MModal>
      ) : null}
    </main>
  )
}
